<template lang="pug">
.x-data-table(id="table")
  v-data-table(
    style="margin-left: -4px !important;"
    item-key="@index"
    :items="rows"
    :headers="cols"
    :height="height"
    :value="selection"
    @input="value => $emit('input',value)"
    :show-select="showSelect && !!rows.length"
    :single-select="singleSelect"
    :search="searchTerm"
    :custom-filter="customSearch"
    :item-class="getRowClass"
    :items-per-page="itemsPerPage"
    @update:items-per-page="itemsPerPage = $event"
    :footer-props="{ itemsPerPageOptions, 'show-first-last-page': true, 'items-per-page-text': 'Itens por página' }"
    dense
    fixed-header
    calculate-widths
    no-data-text="Nenhum dado disponível"
    loading-text="Pesquisando dados..."
  )
    template(
      v-for="col in cols"
      #[`item.${col.value}`]="{ item, index }"
    )
      grid-ex-table-data-cell(
        v-bind="$props"
        :row="item"
        :col="col"
        :row-actions="rowActions"
        :cell-actions="cellActions"
        :states="getColStates(col)"
        @perform="args => $emit('perform', args)"
      )

    template(#item.data-table-select="{ item, isSelected, select }")
      v-simple-checkbox(v-if="!isRowNonSelectable(item)" :value="isSelected" @input="select($event)")

    template(#footer)
      .x-footer
        .x-table.v-data-table__wrapper(v-if="summary")
          table
            tfoot
              tr
                td(width="100%")
                template(v-for="(col, index) in summaryCols")
                  td(:key="`summary-${index}`").x-cell.x-nowrap.px-2.text-end
                    span.text-caption {{ col.text }}
                    br
                    grid-ex-table-data-cell.x-sumario.font-weight-bold(
                      v-bind="$props"
                      :row="summaryRow"
                      :col="col"
                      :states="getColStates(col)"
                      @perform="args => $emit('perform', args)"
                    )

        v-text-field.x-search-field.mx-2(
            v-model="searchTerm"
            single-line
            hide-details
            placeholder="Pesquisar por qualquer campo..."
            clearable
            solo
            dense
            filled
            outlined
            height="30px"
            prepend-inner-icon="mdi-magnify"
            clear-icon="mdi-broom"
          )

</template>

<style lang="scss">
.x-data-table {

  .v-data-footer {
    position: absolute;
    bottom: 8px;
    justify-content: flex-end;
    right: 0;
    bottom: 0;
    margin: 0 !important;
    border: none !important;
    height: 45px !important;
    padding-right: 0;
    .v-data-footer__select {
      margin-left: 0 !important;
      margin-right: 30px !important;
    }
    .v-data-footer__pagination {
      margin-left: 0 !important;
      margin-right: 8px !important;
    }
  }

  .x-footer {

    .x-sumario {
      font-size: 0.8em;
    }

    .x-search-field {
      position: absolute;
      bottom: 8px;
      width: 38%;
      * {
        min-height: initial !important;
      }
      .v-icon {
        zoom: 80% !important;
      }
    }
  }

  .x-row-pointer {
    >>> tbody td:hover {
      cursor: pointer;
    }
  }

  .x-nowrap {
    white-space: nowrap;
  }

  .x-icon {
    padding: 0 !important;
  }

  th i.v-data-table-header__icon {
    position: absolute !important;
    right: -2px;
    top: 30%;
  }
}
</style>

<script>
import { hasRel, findRel } from "@/helpers/rels"
import { normalizeToLowercase } from "@/helpers/strings"

import GridExTableDataCell from './GridExTableDataCell'

export default {
  components: {
    GridExTableDataCell,
  },

  props: {
    site: String,
    eref: String,
    form: Object,

    /**
     * A entidade em exibição.
     * @type {Keep.Paper.Design.Paper}
     */
    entity: Object,

    /**
     * Coleção dos registros selecionados.
     * @type {Array}
     */
    selection: Array,

    /**
     * A coleção de registros exibidos.
     * @type {Keep.Paper.Design.Set}
     */
    set: Object,

    /**
     * O dataset de sumário.
     * @type {Keep.Paper.Design.Set}
     */
    summary: Object,

    /**
     * Controla a exibição da caixa de seleção de registros.
     * @type {Boolean}
     */
    showSelect: Boolean,

    /**
     * Ativa a seleção de único registro em vez de vários.
     * @type {Boolean}
     */
    singleSelect: Boolean,

    /**
     * Altura da grade.
     * @type {Integer | String}
     */
    height: {
      valitadion: v => true,
    },

    /**
     * Ações disponíveis no menu.
     *
     * Esta propriedade recebe um objeto que contém a estrutura de ações organizadas no menu.
     *
     * @type {Object}
     * @property {Array} all - Todas as ações no menu.
     * @property {Array} main - Ações principais no menu.
     * @property {Array} side - Ações secundárias no menu.
     * @property {Array} rest - Outras ações no menu.
     * @property {Array} categories
     *    - Categorias personalizadas no menu, cada uma contendo um nome e um conjunto de ações.
     *
     * @example
     * {
     *   menu: {
     *     all: [...], // Todas as ações da página
     *     main: [...], // Ações principais da página
     *     side: [...], // Ações secundárias da página
     *     rest: [...], // Outras ações da página
     *     categories: [
     *       { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *       { name: 'Category2', actions: [...] },
     *       // ...
     *     ]
     *   },
     *   pins: {
     *     all: [...], // Todas as ações fixadas da página
     *     main: [...], // Ações principais fixadas da página
     *     side: [...], // Ações secundárias fixadas da página
     *     rest: [...], // Outras ações fixadas da página
     *     categories: [
     *       { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *       { name: 'Category2', actions: [...] },
     *       // ...
     *     ]
     *   },
     *   states: {
     *     all: [...], // Todos os monitores de estado da página
     *     main: [...], // Monitores de estado da página
     *     side: [...], // Monitores de estado secundários da página
     *     rest: [...], // Outros monitores de estado da página
     *     categories: [
     *       { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *       { name: 'Category2', actions: [...] },
     *       // ...
     *     ]
     *   }
     * }
     */
    rowActions: Object,
    cellActions: Object,
  },

  data: () => ({
    lastSelectedItem: null,
    shiftKey: false,
    searchTerm: '',
    windowHeight: window.innerHeight,
    currentItemsPerPage: 50
  }),

  computed: {
    states() {
      // Contém estilização de colunas e linhas.
      // Exemplo:
      //    {
      //      states: [
      //        {
      //          "name": "statusUsuario",  // Nome da coluna a qual o estado se aplica.
      //          "icon": "mdi-account",
      //          "expression": "color: row.online ? 'Success' : 'Trivial';"
      //        }
      //      ]
      //    }
      return this.set.states || []
    },

    itemsPerPage: {
      get() {
        // return this.currentItemsPerPage ||= this.computeItemsPerPage()
        return this.currentItemsPerPage
      },
      set(value) {
        this.currentItemsPerPage = value
      }
    },

    itemsPerPageOptions() {
      const options = [
        -1,
        10, 20, 30, 50,
        100, 200, 300, 500,
        1000, 2000, 3000, 5000,
        10000,
        // this.itemsPerPage, this.computeItemsPerPage()
      ]
      options.sort((a, b) => a - b)
      return options
    },

    // Coleção das colunas da grade criadas a partir dos cabeçalhos do Set...
    cols() {
      const rawCols = this.set?.headers || []
      const visibleHeaders = rawCols.filter((header) => !header.hidden)

      let cols = [
        ...visibleHeaders.map((header) => this.createColumn(header)),
        // // Coluna de espaçamento, sem conteúdo.
        // this.createColumn({
        //   name: "#spacer",
        //   title: " ",
        //   nonSortable: true,
        //   width: '99%',
        // })
      ]

      // if (this.actions.menu.all.length) {
        cols = [
          // Links à esquerda.
          this.createColumn({
            name: "#left-actions",
            title: " ",
            class: 'x-icon',
            nonSortable: true,
          }),

          ...cols,

          // Links à direita.
          this.createColumn({
            name: "#right-actions",
            title: " ",
            class: 'x-icon',
            nonSortable: true,
            width: '99%',
          })
        ]
      // }
      return cols
    },

    rows() {
      return this.set?.rows || []
    },

    // Coleção das colunas da grade criadas a partir dos cabeçalhos do Set...
    summaryCols() {
      const rawCols = this.summary?.headers || []
      const visibleHeaders = rawCols.filter((header) => !header.hidden)
      const cols = [
        ...visibleHeaders.map((header) => this.createColumn(header)),
      ]
      return cols
    },

    summaryRow() {
      return this.summary?.rows?.[0] || {}
    },
  },

  methods: {
    // header: Uma instância de {Keep.Tools.Design.Header}.
    createColumn(header) {
      let useBadge = hasRel(header, 'Badge')
      let useIcon = hasRel(header, 'Icon')
      if (useIcon) header.class = 'x-icon'
      let col = {

        // Propriedades disponíveis:
        // - text: string,
        // - value: string,
        // - align?: 'start' | 'center' | 'end',
        // - sortable?: boolean,
        // - filterable?: boolean,
        // - groupable?: boolean,
        // - divider?: boolean,
        // - width?: string | number,
        // - filter?: (value: any, search: string, item: any) => boolean,
        // - sort?: (a: any, b: any) => number

        // data table properties
        value: header.name,
        text: header.title || header.name,
        sortable: !header.nonSortable,
        align: useIcon ? 'center' : this.getAlign(header),
        cellClass: header.class,
        width: header.width,

        // custom properties
        header,
        useBadge,
        useIcon,
      }

      return col
    },

    //
    // * Tratamentos de tecla...
    //
    registerKeyHandler(register) {
      if (register) {
        window.addEventListener("keydown", this.keyHandler)
        window.addEventListener("keyup", this.keyHandler)
      } else {
        window.removeEventListener("keydown", this.keyHandler)
        window.removeEventListener("keyup", this.keyHandler)
      }
    },

    keyHandler(event) {
      if (event.key === "Shift") {
        this.shiftKey = event.type === "keydown"
      }
    },

    isRowNonSelectable(row) {
      return row?.['@row']?.nonSelectable || hasRel(row?.['@row'], "Summary")
    },

    getRowClass(row) {
      // const link = this.getRowLink(row)
      return {
        'text-no-wrap': true,
        // 'x-link': !!link,
      }
    },

    getColStates(col) {
      const name = normalizeToLowercase(col.header.name)
      return this.states.filter(state => !state.name || normalizeToLowercase(state.name) === name)
    },

    call(link, selection) {
      if (link) this.$emit('perform', { link, selection })
    },

    getAlign(header) {
      if (header.name.includes('#left')) return 'left'
      if (header.name.includes('#right')) return 'right'
      if (header.align === 1 || header.align === 'Left') return 'left'
      if (header.align === 2 || header.align === 'Center') return 'center'
      if (header.align === 3 || header.align === 'Right') return 'right'
      return {
        1: "start",
        2: "center",
        3: "end",
        Left: "start",
        Center: "center",
        Right: "end",
      }[header.align]
        || {
          Boolean: "center",
          Integer: "end",
          Long: "end",
          Decimal: "end",
          Date: "center",
          Time: "center",
          DateTime: "center",
          TimeSpan: "center",
        }[header.dataType]
        || "start"
    },

    /**
     * Retorna o tipo de dado de uma célula, analisando as propriedades da coluna e o
     * valor da célula.
     * @param {*} value
     *    O valor da célula.
     * @param {*} col
     *    A coluna da célula.
     */
    dataType(value, col) {
      const dataType = col?.header.dataType
      if (dataType && dataType !== 'Object') return dataType || 'String'

      if (typeof value === 'boolean') return 'Boolean'
      if (typeof value === 'number') return value.toString().includes('.') ? 'Decimal' : 'Integer'

      if (value) {
        if (value.match(/^\d{4}-\d{2}-\d{2}T00:00:00$/)) return 'Date'
        if (value.match(/^\d{4}-\d{2}-\d{2}$/)) return 'Date'
        if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) return 'DateTime'
        if (value.match(/^\d{1,2}:\d{2}:\d{2}/)) return 'Time'
        if (value.match(/^\d+.\d{1,2}:\d{2}:\d{2}/)) return 'TimeSpan'
      }

      return 'String'
    },

    /**
     * Algoritmo de pesquisa customizado para a grade.
     * O algoritmo suporta a pesquisa por qualquer campo da grade ou por um campo específico.
     * Sintaxe:
     *    - Pesquisa por qualquer campo: <termo>
     *    - Pesquisa por um campo específico: <campo>:<termo>
     * Exemplos:
     *    - Pesquisa por qualquer campo: "joão"
     *    - Pesquisa por um campo específico: "nome:joão"
     *    - Pesquisa por vários campos específicos: "nome:joão idade:18"
     *    - Pesquisa por um campo geral e vários campos específicos: "123 nome:joão idade:18"
     * @param {*} _
     *    O valor da célula a ser pesquisado.
     * @param {*} search
     *    O termo de pesquisa.
     * @param {*} row
     *    O registro que contém a célula a ser pesquisada.
     */
    customSearch(_, search, row) {
      search = search.replace(/✓/g, "1");
      search = search.replace(/×/g, "0");

      const searchTokens = this.createSearchTokens(normalizeToLowercase(search))

      // Pesquisa pelo caso geral, aplicado a todos os campos.
      if (searchTokens.general?.length > 0) {
        const matchGeneralToken = searchTokens.general.every(token => {
          if (!token) {
            return true
          }

          const isNegation = token.startsWith('!')
          const searchTerm = isNegation
            ? normalizeToLowercase(token.substring(1))
            : normalizeToLowercase(token)

          // Escolhe o método adequado baseado em isNegation
          const arrayMethod = isNegation ? 'every' : 'some'

          const keys = this.cols.map(col => col.header?.name).filter(key => key in row)
          const isMatch = keys[arrayMethod](key => {
            const value = typeof row[key] === 'boolean' ? (row[key] === true ? 1 : 0) : row[key]
            let target = normalizeToLowercase(value)
            if (target === '✓' || target === '×') {
              target = target === '✓' ? '1' : '0'
            }
            const cellContainsToken = !!target?.includes(searchTerm)
            return isNegation !== cellContainsToken
          })
          return isMatch
        })

        if (!matchGeneralToken) {
          return false
        }
      }

      // Pesquisa por campo especificao, quando informado na forma "campo:valor".
      for (let key in searchTokens) {
        const value = typeof row[key] === 'boolean' ? (row[key] === true ? 1 : 0) : row[key]
        if (key !== 'general' && value !== undefined) {
          const matchFieldToken = searchTokens[key].every(token => {
            if (!token) {
              return true
            }

            const isNegation = token.startsWith('!')
            const searchTerm = isNegation
              ? normalizeToLowercase(token.substring(1))
              : normalizeToLowercase(token)

            let target = normalizeToLowercase(value)
            if (target === '✓' || target === '×') {
              target = target === '✓' ? '1' : '0'
            }

            const cellContainsToken = !!normalizeToLowercase(target)?.includes(searchTerm)
            return isNegation !== cellContainsToken
          })
          if (!matchFieldToken) {
            return false
          }
        }
      }

      return true
    },

    /**
     * Cria os tokens de pesquisa a partir do termo de pesquisa.
     * O termo de pesquisa pode conter um ou mais campos específicos.
     * Agora, também suporta múltiplos valores para o mesmo campo.
     * Sintaxe:
     *    - Pesquisa por qualquer campo: <termo>
     *    - Pesquisa por um campo específico: <campo>:<termo>
     *    - Pesquisa por um campo específico com múltiplos valores: <campo>:<termo1> <campo>:<termo2>
     * Este método retorna um objeto com os tokens de pesquisa, onde cada campo é mapeado para uma lista de valores.
     * Exemplos:
     *    - Pesquisa por qualquer campo: "joão"
     *    - Resultado:
     *        {
     *          general: ["joão"]
     *        }
     *    - Pesquisa por um campo específico: "nome:joão"
     *    - Resultado:
     *        {
     *          nome: ["joão"]
     *        }
     *    - Pesquisa por um campo específico com múltiplos valores: "login:meng login:pereng"
     *    - Resultado:
     *        {
     *          login: ["meng", "pereng"]
     *        }
     *
     * @param {*} search
     *    O termo de pesquisa.
     */
    createSearchTokens(search) {
      let tokens = {}
      let parts = search.match(/(?:[\w-]+:[^:]+?(?=\s\w+:|$))|(?:\S+)/g) || []

      parts.forEach(part => {
        if (part.includes(':')) {
          let [field, value] = part.split(':').map(v => v.trim())
          if (!tokens[field]) {
            tokens[field] = []
          }
          tokens[field].push(value)
        } else {
          if (!tokens.general) {
            tokens.general = []
          }
          tokens.general.push(part)
        }
      })
      return tokens
    },

    handleResize() {
      this.windowHeight = window.innerHeight
    },

    // computeItemsPerPage() {
    //   const offset = 230
    //   const pixelsPerItem = 34
    //   const visibleHeight = this.windowHeight - offset
    //   return Math.floor(visibleHeight / pixelsPerItem)
    // },
  },

  watch: {
  },

  created () {
    this.registerKeyHandler(true)

    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },

  beforeDestroy () {
    this.registerKeyHandler(false)
    window.removeEventListener('resize', this.handleResize)
  },
}
</script>
